import _ from 'lodash';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Input,
} from '@heroui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { setUnitPref } from '@/patient-app-common/slices/appSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

export function HeightInput({ sendAnswer, answerHistory }) {
  const dispatch = useAppDispatch();
  let { unitPref } = useAppSelector(({ app }) => app);

  if (!unitPref) {
    unitPref = 'metric';
  }

  const [unit, setUnit] = useState(unitPref);

  const handleUnitChange = () => {
    if (unit === 'metric') {
      setUnit('imperial');
      sendAnswer(undefined);
      dispatch(setUnitPref('imperial'));
    } else {
      setUnit('metric');
      sendAnswer(undefined);
      dispatch(setUnitPref('metric'));
    }
  };

  return (
    <Card className="max-w-md">
      <CardBody>
        {unit === 'imperial' && (
          <ImperialInput
            sendAnswer={sendAnswer}
            answerHistory={answerHistory}
          />
        )}
        {unit === 'metric' && (
          <MetricInput sendAnswer={sendAnswer} answerHistory={answerHistory} />
        )}
      </CardBody>
      <CardFooter>
        <ButtonGroup>
          <Button
            color="primary"
            onPress={handleUnitChange}
            variant={unit === 'imperial' ? 'solid' : 'bordered'}
          >
            Feet and inches
          </Button>
          <Button
            color="primary"
            onPress={handleUnitChange}
            variant={unit === 'metric' ? 'solid' : 'bordered'}
          >
            Centimetres (cm)
          </Button>
        </ButtonGroup>
      </CardFooter>
    </Card>
  );
}

function ImperialInput({ answerHistory, sendAnswer }) {
  const [errorFt, setErrorFt] = useState('');
  const [errorInch, setErrorInch] = useState('');

  const [height, setHeight] = useState({
    feet: '',
    inches: '',
  });

  const convertInitAnswer = useCallback(() => {
    if (answerHistory) {
      const totalInches = answerHistory * 0.3937;
      let feet = totalInches / 12;
      let inches = totalInches % 12;

      if (inches > 11) {
        feet += 1;
        inches = 0;
      }

      setHeight({
        feet: _.floor(feet),
        inches: _.round(inches),
      });
    }
  }, [answerHistory]);

  useEffect(() => {
    convertInitAnswer();
  }, []);

  const validateNumber = (value, input) => {
    switch (input) {
      case 'feet':
        if (!Number.isFinite(value)) {
          setErrorFt('Enter a number for feet');
          return false;
        }
        break;

      case 'inches':
        if (!Number.isFinite(value)) {
          setErrorInch('Enter a number for inches');
          return false;
        }

        if (value >= 12) {
          setErrorInch('Enter 12 inches as 1 foot');
          return false;
        }
        break;

      default:
    }

    return true;
  };

  const handleInputChange = (value, input) => {
    const valid = validateNumber(value, input);

    if (!valid) {
      setHeight({
        ...height,
        [input]: undefined,
      });
      return;
    }

    if (input === 'feet') {
      setErrorFt('');
    } else {
      setErrorInch('');
    }

    setHeight({
      ...height,
      [input]: value.toString(),
    });
  };

  useEffect(() => {
    const convertToCM = () => {
      const { feet, inches } = height;
      const cm = parseFloat(feet) * 30.48 + parseFloat(inches) * 2.54;
      return _.round(cm, 2);
    };

    const validateAnswer = () => {
      const { feet, inches } = height;

      if (!feet || !inches) {
        sendAnswer(undefined);
      } else {
        setErrorFt('');
        setErrorInch('');

        const answer = convertToCM().toString();
        sendAnswer(answer);
      }
    };

    validateAnswer();
  }, [height]);

  return (
    <div className="flex flex-col">
      <Input
        color="primary"
        errorMessage={errorFt}
        isInvalid={!_.isEmpty(errorFt)}
        label="Feet"
        onChange={(e) => handleInputChange(parseFloat(e.target.value), 'feet')}
        type="number"
        value={height.feet?.toString()}
        variant="underlined"
      />
      <Input
        color="primary"
        errorMessage={errorInch}
        isInvalid={!_.isEmpty(errorInch)}
        label="Inches"
        onChange={(e) =>
          handleInputChange(parseFloat(e.target.value), 'inches')
        }
        type="number"
        value={height.inches?.toString()}
        variant="underlined"
      />
    </div>
  );
}

function MetricInput({ sendAnswer, answerHistory }) {
  const [error, setError] = useState('');

  const validateNumber = (value) => {
    if (!Number.isFinite(value)) {
      setError('Enter a number');
      return false;
    }
    return true;
  };

  const handleInputChange = (value) => {
    setError('');
    const valid = validateNumber(value);

    if (!valid) {
      sendAnswer(null);
      return;
    }

    sendAnswer(_.round(value, 2).toString());
  };

  return (
    <Input
      color="primary"
      value={answerHistory}
      endContent={
        <div className="flex items-center">
          <span>cm</span>
        </div>
      }
      errorMessage={error}
      isInvalid={!_.isEmpty(error)}
      onChange={(e) => handleInputChange(parseFloat(e.target.value))}
      type="number"
      variant="underlined"
    />
  );
}
